import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import HiResAudioBadge from '../HiResAudioBadge';
import { isBadCoverUrl } from '../../utils/coverArt';
import {
  getCoverArtBatch,
  getCoverArtWithFallback,
  getCoverFromCache,
  getRemoteCoverArt,
  getRemoteCoverFromCache,
  isCoverIPCAvailable,
  isLocalLibraryAlbum,
  isRemoteCoverViewerShell,
  normalizeCoverHash,
  pickAlbumFolderPath,
} from '../../lib/coverArtIPC';
import { useShallow } from 'zustand/react/shallow';
import { useCollectorMode } from '../../store/libraryUIStore';
import { useMaestroStore } from '../../store/maestroStore';
import {
  getFormatBadge,
  getSourceBadge,
  isMissingArtwork,
  isDsd,
  isHiRes,
} from '../../utils/formatDetection';
import { isScanActive } from '../../services/libraryScanCoordinator';

// Zustand v5's `useStore(selector, equalityFn)` two-arg form was REMOVED — the
// equality function is silently dropped and React.useSyncExternalStore falls
// back to Object.is. Returning a fresh object literal from the selector then
// re-fires every commit, producing the "getSnapshot should be cached"
// warning followed by React #185 ("Maximum update depth exceeded"). The
// canonical fix is `useShallow`, which caches the prior reference and returns
// it when the new selector result shallow-equals it (same pattern as the
// be870c72 HiddenAlbums fix, just expressed via the shallow helper instead
// of an explicit useMemo because here we're synthesising a new object from
// multiple store fields rather than projecting a stored container).
function useNowPlayingAlbumKey() {
  return useMaestroStore(
    useShallow((s) => {
      const t = s.currentTrack;
      if (!t || s.playbackState === 'stopped') return null;
      return {
        albumId: String(t.albumId || t.album_id || t.serviceAlbumId || ''),
        albumTitle: (t.album || t.albumTitle || '').toLowerCase().trim(),
        artistName: (t.artist || t.artistName || '').toLowerCase().trim(),
      };
    }),
  );
}

function isNowPlaying(album, npKey) {
  if (!npKey) return false;
  const id = String(album?.id || album?.album_key || '');
  if (npKey.albumId && id && npKey.albumId === id) return true;
  const title = (album?.title || album?.album || '').toLowerCase().trim();
  const artist = (album?.artist_name || album?.album_artist || album?.artist || '').toLowerCase().trim();
  return !!(title && artist && title === npKey.albumTitle && artist === npKey.artistName);
}

export function hashColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const h = Math.abs(hash) % 360;
  return `hsl(${h}, 35%, 25%)`;
}

export function placeholderDataUri(title) {
  const color = hashColor(title);
  const safe = String(title || 'Untitled').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const lines = safe.length > 30 ? [safe.slice(0, 20), safe.slice(20, 40)] : [safe];
  const textY = lines.length > 1 ? 185 : 200;
  const textSvg = lines.map((l, i) =>
    `<text x="200" y="${textY + i * 36}" font-family="system-ui,sans-serif" font-size="28" font-weight="800" fill="rgba(255,255,255,0.85)" text-anchor="middle">${l}</text>`
  ).join('');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400"><rect width="400" height="400" fill="${color}"/>${textSvg}</svg>`;
  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
}

/**
 * AlbumGrid — Phase 1 mission-control album wall.
 *
 * Behavior:
 *   - Single click  → onAlbumClick (highlight / open)
 *   - Double click  → onAlbumDoubleClick (PLAY via QueueController upstream)
 *   - Right-click   → onAlbumContextMenu (caller decides — see LibraryBrowser)
 *
 * Visual contract:
 *   - Calm by default. Only the format-quality badge sits on the cover.
 *   - On hover, the source badge (Local/NAS/Qobuz) and any warnings
 *     (missing artwork, duplicate) fade in over 150ms.
 *   - Collector Mode (per-user persisted toggle) makes hover badges
 *     ALWAYS visible — no extra logic required, just a body-class flip.
 *   - Disconnected sources fade the tile (existing behavior preserved).
 */
export default function AlbumGrid({
  albums,
  onAlbumClick,
  onAlbumEdit,
  onAlbumDoubleClick,
  onAlbumContextMenu,
  onAlbumInspect,
  homeLibraryOffline,
  hasMore,
  loadingMore,
  onLoadMore,
  selectedAlbumKey,
  disconnectedSourceIds,
  reconnectingSourceIds,
  versionMap,
}) {
  const list = (Array.isArray(albums) ? albums : []).filter((a) => (a.track_count ?? 1) > 0);
  const npKey = useNowPlayingAlbumKey();
  const sentinelRef = useRef(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const [coverBatchEpoch, setCoverBatchEpoch] = useState(0);

  const coverHashKey = useMemo(() => {
    const seen = new Set();
    const parts = [];
    for (const album of list) {
      const emb = normalizeCoverHash(album?.embedded_cover_hash);
      const fol = normalizeCoverHash(album?.folder_cover_hash);
      for (const h of [emb, fol]) {
        if (h && !seen.has(h)) {
          seen.add(h);
          parts.push(h);
        }
      }
    }
    return parts.join('|');
  }, [list]);

  useEffect(() => {
    const mountT0 = performance.now();
    console.log('[album-grid] mount', { albumCount: list.length, t0: mountT0 });
    return () => {
      console.log('[album-grid] unmount', { ms: Math.round(performance.now() - mountT0) });
    };
  }, []);

  const isRemoteViewerShell = isRemoteCoverViewerShell();

  useEffect(() => {
    if (!coverHashKey || isScanActive()) return;
    const hashes = coverHashKey.split('|').filter(Boolean).slice(0, 48);
    if (hashes.length === 0) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (isRemoteViewerShell) {
        Promise.all(hashes.map((h) => getRemoteCoverArt(h, null))).then(() => {
          if (!cancelled) setCoverBatchEpoch((n) => n + 1);
        });
        return;
      }
      if (!isCoverIPCAvailable()) return;
      getCoverArtBatch(hashes).then(() => {
        if (!cancelled) setCoverBatchEpoch((n) => n + 1);
      });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [coverHashKey, isRemoteViewerShell]);

  const handleIntersect = useCallback((entries) => {
    if (entries[0]?.isIntersecting) onLoadMoreRef.current?.();
  }, []);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(handleIntersect, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, handleIntersect]);

  // Auto-trigger loadMore shortly after mount when the sentinel is below the
  // fold and the IntersectionObserver hasn't fired yet. Fixes the "fills
  // screen and stops" issue for large libraries.
  useEffect(() => {
    if (!hasMore) return;
    const timer = setTimeout(() => {
      const el = sentinelRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const inViewport = rect.top < window.innerHeight + 400;
      if (!inViewport) onLoadMoreRef.current?.();
    }, 500);
    return () => clearTimeout(timer);
  }, [hasMore, list.length]);

  return (
    <div
      className="grid gap-4 pb-28 px-3 sm:px-4"
      style={{
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
      }}
    >
      {list.map((album, idx) => {
        const isLocal = album?.__source === 'local_library' || album?.isLocalLibrary === true;
        const unavailable = (isLocal && homeLibraryOffline)
          || (album?.source_id && disconnectedSourceIds?.has?.(album.source_id));
        const isReconnecting = album?.source_id && reconnectingSourceIds?.has?.(album.source_id);
        const key = album.id || album.album_key || idx;
        const isSelected = selectedAlbumKey != null && (selectedAlbumKey === album.album_key || selectedAlbumKey === album.id);
        const isPlaying = isNowPlaying(album, npKey);
        return (
          <AlbumCard
            key={key}
            album={album}
            coverBatchEpoch={coverBatchEpoch}
            unavailable={unavailable}
            isReconnecting={isReconnecting}
            isSelected={isSelected}
            isPlaying={isPlaying}
            versionCount={versionMap?.get?.(album.album_key || album.id)?.group?.versions?.length || 0}
            onClick={() => !unavailable && onAlbumClick?.(album)}
            onDoubleClick={() => !unavailable && (onAlbumDoubleClick || onAlbumClick)?.(album)}
            onEdit={() => !unavailable && onAlbumEdit?.(album)}
            onContextMenu={(e) => !unavailable && onAlbumContextMenu?.(e, album)}
            onInspect={() => !unavailable && onAlbumInspect?.(album)}
          />
        );
      })}
      {hasMore && (
        <div ref={sentinelRef} className="col-span-full flex justify-center py-8">
          {loadingMore ? (
            <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          ) : (
            <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Scroll for more…</div>
          )}
        </div>
      )}
    </div>
  );
}

const PILL = {
  background: 'rgba(0,0,0,0.75)',
  color: '#FFFFFF',
  fontSize: 11,
  fontWeight: 600,
  padding: '3px 8px',
  borderRadius: 4,
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
};
const PILL_WARN = {
  ...PILL,
  background: 'rgba(180,120,0,0.85)',
};

function AlbumCard({ album, coverBatchEpoch = 0, onClick, onDoubleClick, onEdit, onContextMenu, onInspect, unavailable, isReconnecting, isSelected, isPlaying, versionCount }) {
  const collectorMode = useCollectorMode();

  const status = album?.enrichment_status || (album?.enriched_at ? 'enriched' : 'pending');
  const showDot = status !== 'pending';
  const dotColor = {
    enriched: '#34d399',
    user_edited: '#34d399',
    needs_review: '#fbbf24',
    unidentified: '#fb7185',
    enriching: '#38bdf8',
  }[status] || null;

  const embHash = normalizeCoverHash(album?.embedded_cover_hash);
  const folHash = normalizeCoverHash(album?.folder_cover_hash);
  const albumFolderPath = pickAlbumFolderPath(album);
  const isRemoteViewerShell = isRemoteCoverViewerShell();
  const isRemoteTunnel =
    isRemoteViewerShell
    || album?.__source === 'remote_tunnel'
    || !!(album?.cover_art_url && String(album.cover_art_url).includes('/api/library/remote/cover/'));
  const useIpcOnly = isCoverIPCAvailable() && isLocalLibraryAlbum(album) && !isRemoteTunnel;

  const onlineCoverRaw = album?.cover_art_url || album?.coverUrl || album?.cover_url || null;
  const coverBust = album?.cover_art_updated_at || album?.updated_at || album?.cover_art_source || null;
  const onlineCoverHttps = String(onlineCoverRaw || '').startsWith('http://')
    ? String(onlineCoverRaw).replace(/^http:\/\//i, 'https://')
    : onlineCoverRaw;
  const cleanOnlineCover = isBadCoverUrl(onlineCoverHttps)
    || (isRemoteTunnel && String(onlineCoverHttps || '').includes('/api/library/remote/cover/'))
    ? null
    : onlineCoverHttps;
  const onlineCover =
    cleanOnlineCover && coverBust
      ? `${cleanOnlineCover}${String(cleanOnlineCover).includes('?') ? '&' : '?'}v=${encodeURIComponent(String(coverBust))}`
      : cleanOnlineCover;

  const overrideSource = album?.user_override_source;

  const initialIpc = isRemoteTunnel
    ? (getRemoteCoverFromCache(embHash, album?.machine_id) ?? getRemoteCoverFromCache(folHash, album?.machine_id) ?? undefined)
    : (getCoverFromCache(embHash) ?? getCoverFromCache(folHash) ?? undefined);
  const [localCoverUrl, setLocalCoverUrl] = useState(
    initialIpc !== undefined ? (initialIpc || null) : null,
  );
  const coverLoadedRef = useRef(false);
  useEffect(() => {
    coverLoadedRef.current = false;
  }, [embHash, folHash, albumFolderPath, album?.album_key]);
  useEffect(() => {
    if (isRemoteTunnel) {
      const hash = embHash || folHash;
      if (!hash) {
        setLocalCoverUrl(null);
        return;
      }
      const cachedRemote = getRemoteCoverFromCache(hash, album?.machine_id)
        ?? getRemoteCoverFromCache(hash, null);
      if (cachedRemote !== undefined) {
        setLocalCoverUrl(cachedRemote || null);
        coverLoadedRef.current = true;
        return;
      }
      if (coverLoadedRef.current) return;
      let cancelled = false;
      coverLoadedRef.current = true;
      (async () => {
        const url = await getRemoteCoverArt(hash, album?.machine_id || null);
        if (!cancelled) setLocalCoverUrl(url);
      })();
      return () => { cancelled = true; };
    }

    const cached = getCoverFromCache(embHash) ?? getCoverFromCache(folHash);
    if (cached !== undefined) {
      setLocalCoverUrl(cached || null);
      coverLoadedRef.current = true;
      return;
    }
    if (coverLoadedRef.current) return;
    if (useIpcOnly || isCoverIPCAvailable()) {
      let cancelled = false;
      coverLoadedRef.current = true;
      (async () => {
        const url = await getCoverArtWithFallback(embHash, folHash, albumFolderPath, {
          albumId: album?.album_key || album?.id || null,
        });
        if (!cancelled) setLocalCoverUrl(url);
      })();
      return () => { cancelled = true; };
    }
    setLocalCoverUrl(null);
  }, [embHash, folHash, albumFolderPath, album?.album_key, album?.id, album?.machine_id, useIpcOnly, isRemoteTunnel, coverBatchEpoch]);

  let coverUrl;
  if (overrideSource === 'embedded') {
    coverUrl = localCoverUrl || onlineCover || null;
  } else if (overrideSource === 'folder') {
    coverUrl = localCoverUrl || onlineCover || null;
  } else if (overrideSource === 'online') {
    coverUrl = onlineCover || localCoverUrl || null;
  } else if (overrideSource === 'none') {
    coverUrl = null;
  } else if (isRemoteTunnel) {
    // JWT-protected API URLs must not go in <img src> — loaded via fetchCloudAuthenticated above.
    coverUrl = localCoverUrl;
  } else {
    coverUrl = localCoverUrl || onlineCover;
  }
  const title = String(album?.title || '').trim() || 'Untitled';
  const artist = String(album?.artist_name || '').trim() || 'Unknown Artist';

  const formatText = getFormatBadge(album);
  const sourceText = getSourceBadge(album);
  const missingArt = isMissingArtwork(album);
  const dsd = isDsd(album);
  const hires = isHiRes(album);
  const drValue = album?.dynamic_range ? `DR${album.dynamic_range}` : null;

  const [imgErrorKey, setImgErrorKey] = useState(0);
  const triedOnlineRef = useRef(false);
  const prevCoverRef = useRef(coverUrl);
  if (prevCoverRef.current !== coverUrl) {
    prevCoverRef.current = coverUrl;
    triedOnlineRef.current = false;
    if (imgErrorKey !== 0) setImgErrorKey(0);
  }

  let activeUrl = coverUrl;
  if (imgErrorKey > 0 && triedOnlineRef.current) {
    activeUrl = null;
  } else if (imgErrorKey > 0 && !triedOnlineRef.current && onlineCover && coverUrl !== onlineCover) {
    activeUrl = onlineCover;
  }

  const handleImgError = useCallback(() => {
    if (isRemoteTunnel) {
      setImgErrorKey(2);
      return;
    }
    if (!triedOnlineRef.current && onlineCover && coverUrl !== onlineCover) {
      triedOnlineRef.current = true;
    }
    setImgErrorKey(k => k + 1);
  }, [onlineCover, coverUrl, isRemoteTunnel]);
  const showCover = !!activeUrl && imgErrorKey < 2;

  const hoverClass = collectorMode
    ? 'opacity-100'
    : 'opacity-0 pointer-fine:group-hover:opacity-100';

  return (
    // Outer wrapper is a div+role=button (not a real <button>) because the
    // "Show Inspector" overlay below is itself a <button>, and a <button>
    // cannot legally contain another <button> per the HTML spec — React DEV
    // logs a validateDOMNesting warning and produces a hydration mismatch.
    // We re-add the keyboard activation that a native button would supply.
    <div
      role="button"
      tabIndex={unavailable ? -1 : 0}
      aria-disabled={unavailable || undefined}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      onKeyDown={(e) => {
        if (unavailable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(e);
        }
      }}
      className={`transition-all duration-200 group w-full text-left p-0 border-0 bg-transparent font-inherit touch-manipulation select-none [-webkit-tap-highlight-color:transparent] ${unavailable ? 'cursor-default' : 'active:scale-[0.97] cursor-pointer'}`}
    >
      <div
        className="relative aspect-square rounded-2xl overflow-hidden"
        style={{
          background: 'var(--bg-secondary)',
          border: (isPlaying || isSelected)
            ? '2px solid var(--accent-primary, #C9A84C)'
            : '1px solid var(--border-light)',
          boxShadow: isPlaying
            ? '0 0 12px rgba(201,168,76,0.3)'
            : isSelected ? '0 4px 20px rgba(201,168,76,0.25)' : 'var(--shadow-card)',
        }}
      >
        {showCover ? (
          <img
            src={activeUrl}
            alt=""
            className="w-full h-full object-cover pointer-fine:group-hover:scale-[1.04] transition-transform duration-300 pointer-events-none"
            loading="lazy"
            draggable={false}
            onError={handleImgError}
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center p-3"
            style={{ backgroundColor: hashColor(title) }}
          >
            <span className="text-white/90 font-extrabold text-center text-sm leading-tight drop-shadow-md line-clamp-3">
              {title}
            </span>
          </div>
        )}

        {/* Top-right: ⓘ inspect button + version badge */}
        <div className="absolute top-2 right-2 flex items-center gap-1.5" style={{ zIndex: 2 }}>
          {versionCount > 1 && (
            <div
              className="flex items-center justify-center pointer-events-none"
              style={{
                width: 20, height: 20, borderRadius: 10,
                background: 'var(--accent-primary, #C9A84C)',
                color: '#000', fontSize: 10, fontWeight: 800,
                boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
              }}
              title={`${versionCount} versions in library`}
            >
              {versionCount}
            </div>
          )}
          {onInspect && (
            <button
              type="button"
              className={`flex items-center justify-center transition-opacity duration-150 ${collectorMode ? 'opacity-100' : 'opacity-0 pointer-fine:group-hover:opacity-100 pointer-coarse:opacity-100'}`}
              style={{
                width: 28, height: 28, borderRadius: 14,
                background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
                border: 'none', cursor: 'pointer', padding: 0,
                boxShadow: '0 1px 6px rgba(0,0,0,0.4)',
              }}
              title="Show Inspector"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onInspect(); }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </button>
          )}
        </div>

        {/* Now-playing equalizer indicator */}
        {isPlaying && (
          <div
            className="absolute top-2 left-2 flex items-end gap-[2px] pointer-events-none"
            style={{ zIndex: 3, height: 14 }}
          >
            <span className="inline-block w-[3px] rounded-sm" style={{ background: '#C9A84C', animation: 'eqBar1 0.8s ease-in-out infinite' }} />
            <span className="inline-block w-[3px] rounded-sm" style={{ background: '#C9A84C', animation: 'eqBar2 0.6s ease-in-out infinite 0.15s' }} />
            <span className="inline-block w-[3px] rounded-sm" style={{ background: '#C9A84C', animation: 'eqBar3 0.75s ease-in-out infinite 0.3s' }} />
          </div>
        )}

        {/* Reconnecting / offline ribbon */}
        {(unavailable || isReconnecting) && (
          <div
            className="absolute top-2 left-2 px-2 py-0.5 rounded pointer-events-none"
            style={{
              ...PILL,
              background: isReconnecting ? 'rgba(201,168,76,0.92)' : 'rgba(239,68,68,0.92)',
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {isReconnecting ? 'Reconnecting' : 'Offline'}
          </div>
        )}

        {/* Badge row — bottom of artwork, 8px inset */}
        <div
          className="absolute bottom-2 left-2 right-2 flex flex-wrap items-end gap-1 pointer-events-none"
        >
          {/* Format badge — always visible */}
          {formatText && (
            <span className="flex items-center gap-1" style={PILL}>
              {hires && !dsd && (
                <HiResAudioBadge bitDepth={album?.bit_depth} sampleRate={album?.sample_rate} height={13} />
              )}
              {formatText}
            </span>
          )}

          {/* Hover / Collector-mode badges */}
          {sourceText && (
            <span className={`transition-opacity duration-150 ${hoverClass}`} style={PILL}>
              {sourceText}
            </span>
          )}
          {drValue && (
            <span className={`transition-opacity duration-150 ${hoverClass}`} style={PILL}>
              {drValue}
            </span>
          )}
          {missingArt && (
            <span className={`transition-opacity duration-150 ${hoverClass}`} style={PILL_WARN}>
              No Art
            </span>
          )}
          {album?.is_duplicate && (
            <span className={`transition-opacity duration-150 ${hoverClass}`} style={PILL_WARN}>
              Dup
            </span>
          )}
        </div>
      </div>

      <div className="mt-3">
        <div className="font-extrabold truncate text-sm sm:text-[15px]" style={{ color: 'var(--text-primary)' }}>{title}</div>
        <div className="truncate text-xs sm:text-sm" style={{ color: 'var(--text-secondary)' }}>{artist}</div>
        {album?.is_compilation && (
          <div className="flex gap-1.5 mt-1">
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-purple-300/70 bg-purple-500/10 border border-purple-400/15">
              Mix
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
